import { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslationApp } from "../../lib/i18next";
import { InputAdornment } from "@mui/material";
import Loader from "../Loaders/Loader";
import { BUTTON_SEND, FIELD_MAX_LENGTH } from "../../i18n/keysTranslations";
import TextField from "../Form/TextField";
import { useForm } from "react-hook-form";

const CommonChatMessageInput = ({
  onSendMessage,
  disabled = false,
  placeholderInput,
  defaultQuestion = "",
  sx,
  multiline = true,
  messageVariant,
}) => {
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [file, setFile] = useState(null);
  const [showAlertUsage, setShowAlertUsage] = useState(true);
  const [hasSetDefaultQuestion, setHasSetDefaultQuestion] = useState(false);

  const { t } = useTranslationApp();

  // React Hook Form setup
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      message: "",
    },
  });

  const message = watch("message");

  // Set default question only once when it is received
  useEffect(() => {
    if (defaultQuestion && !hasSetDefaultQuestion) {
      setValue("message", defaultQuestion);
      setHasSetDefaultQuestion(true);
    }
  }, [defaultQuestion, hasSetDefaultQuestion, setValue]);

  useEffect(() => {
    if (message?.length > 1000) {
      setError(t(FIELD_MAX_LENGTH, { value: 1000 }));
      return;
    }

    if (error) {
      setError(null);
    }
  }, [message, error, t]);

  const onClickSendMessage = async () => {
    if (message.length > 1000) {
      setError(t(FIELD_MAX_LENGTH, { value: 1000 }));
      return false;
    }
    if (!message && file === null) return false;

    if (showAlertUsage) {
      setShowAlertUsage(false);
    }

    const messageFormatted = message.replace("&nbsp;", " ").trim();
    const currentFile = file;

    const response = await onSendMessage(messageFormatted, currentFile);
    if (response) {
      setValue("message", "");
      setFile(null);
    }
  };

  const getCustomSxByVariant = () => {
    if (messageVariant === "inline") {
      return {
        p: "0px !important",
      };
    }
    return {};
  };

  const isSendDisabled = (message === "" && file === null) || disabled;

  const renderSendControl = () => {
    if (isSending) {
      return <Loader hasMarginTop={false} size={18} />;
    }

    return (
      <Typography
        component="button"
        type="button"
        variant="body2"
        onClick={async (e) => {
          if (isSendDisabled) {
            return;
          }
          setIsSending(true);
          await onClickSendMessage(e);
          setIsSending(false);
        }}
        disabled={isSendDisabled}
        sx={{
          fontWeight: 700,
          border: "none",
          background: "none",
          padding: 0,
          cursor: isSendDisabled ? "default" : "pointer",
          color: isSendDisabled ? "text.disabled" : "text.primary",
          "&:hover": {
            color: isSendDisabled ? "text.disabled" : "text.primary",
          },
        }}
      >
        {t(BUTTON_SEND)}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
      onKeyDown={(e) => {
        if ((e.code === "Enter" || e.code === "NumpadEnter") && !e.shiftKey) {
          e.preventDefault();
          onClickSendMessage();
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <TextField
          control={control}
          name="message"
          disabled={disabled}
          placeholder={t(placeholderInput)}
          multiline={multiline}
          minRows={multiline ? 1 : undefined}
          maxRows={multiline ? 6 : undefined}
          fullWidth
          variant="filled"
          rules={{}}
          errors={errors}
          sx={{
            ...sx,
            ...getCustomSxByVariant(),
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ alignSelf: "center", alignItems: "center" }}
              >
                {renderSendControl()}
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {error && (
        <Typography variant="caption" sx={{ color: "error.main", mt: 1 }}>
          {error}
        </Typography>
      )}
      {file && (
        <Box
          sx={{
            px: "22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: "2px",
            pb: "-2px",
            mb: 1,
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="body2"
              align="center"
              sx={{ mb: "2px", color: "text.primary" }}
            >
              {file.name}
            </Typography>
          </Box>
          <IconButton sx={{ p: 0 }} onClick={() => setFile(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default CommonChatMessageInput;
